import type { HTMLAttributes } from "react";
import React from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import type { AccountingTransaction } from "../../../../../shared/domains/accounting-transaction/transaction/transaction";
import { MainColorSpinner } from "../../../common/spinner";
import { theme } from "../../../styles/theme";
import { TransactionRow } from "./transaction-row";

interface LatestTransactionsSectionProps extends HTMLAttributes<HTMLDivElement> {
  transactions: AccountingTransaction[];
  loading: boolean;
  onTransactionClick: (transaction: AccountingTransaction) => void;
}

export function LatestTransactionsSection(props: Readonly<LatestTransactionsSectionProps>) {
  const { transactions, loading, onTransactionClick, ...rest } = props;
  const { formatMessage } = useIntl();

  return (
    <Section {...rest}>
      <Title>{formatMessage("homeScreen.latestTransactionsTitle")}</Title>
      {loading ? (
        <LoaderRow>
          <MainColorSpinner size="24px" />
        </LoaderRow>
      ) : transactions.length === 0 ? (
        <EmptyLabel>{formatMessage("accountTransactions.emptyTransactionList")}</EmptyLabel>
      ) : (
        transactions.map((transaction) => (
          <TransactionRow
            key={transaction.id}
            transaction={transaction}
            onClick={() => onTransactionClick(transaction)}
          />
        ))
      )}
    </Section>
  );
}

const Section = styled.section`
  display: flex;
  flex-direction: column;
  align-self: stretch;
`;

const Title = styled.h2`
  ${theme.mediumText};
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 18px;
  color: ${theme.colors.gray[800]};
`;

const LoaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
`;

const EmptyLabel = styled.span`
  ${theme.text};
  color: ${theme.colors.gray[500]};
`;
